import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
    alpha,
    AppBar,
    Box,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Switch,
    Tab,
    Tabs,
    Theme,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { SextforceAutoRepostWithLatestRepostLog } from '../../../../hooks/useSextforceAutoRepost';
import useSextforceAutoRepostColors from '../../../../hooks/useSextforceAutoRepostColors';
import { OnlyFansSubscriber } from '../../../../hooks/useSubscriber';
import SextforceAutoRepostCalendarDayView from './SextforceAutoRepostCalendarDayView';
import SextforceAutoRepostCalendarListView from './SextforceAutoRepostCalendarListView';

export interface TaskOccurrence {
    task: SextforceAutoRepostWithLatestRepostLog;
    dateTime: moment.Moment;
    isRepeat: boolean;
}

type Props = {
    subscriber: OnlyFansSubscriber;
    autoReposts: SextforceAutoRepostWithLatestRepostLog[];
    autoRepostsLoading: boolean;
    refetchAutoReposts: () => void;
    active: boolean;
    sortBy: 'name' | '_id' | 'description' | 'nextRepostDate';
    setAutoRepostInactiveStatus: (autoRepostId: string, inactive: boolean) => Promise<{ success: boolean }>;
};

const SextforceAutoRepostCalendar = ({
    subscriber,
    autoReposts,
    autoRepostsLoading,
    refetchAutoReposts,
    active,
    sortBy,
    setAutoRepostInactiveStatus,
}: Props) => {
    const theme: Theme = useTheme();
    const [currentDate, setCurrentDate] = useState<moment.Moment>(
        (localStorage.getItem('sextforceAutoRepostCalendarViewCurrentDate') &&
            moment(localStorage.getItem('sextforceAutoRepostCalendarViewCurrentDate'))) ||
            moment(),
    );
    const [selectedDate, setSelectedDate] = useState<moment.Moment>(
        (localStorage.getItem('sextforceAutoRepostCalendarViewSelectedDate') &&
            moment(localStorage.getItem('sextforceAutoRepostCalendarViewSelectedDate'))) ||
            moment(),
    );
    const [view, setView] = useState<'month' | 'day' | 'list'>(
        (localStorage.getItem('sextforceAutoRepostCalendarView') as 'month' | 'day' | 'list') || 'month',
    );
    const [showFutureReposts, setShowFutureReposts] = useState<boolean>(
        (localStorage.getItem('sextforceAutoRepostCalendarShowFutureReposts') &&
            localStorage.getItem('sextforceAutoRepostCalendarShowFutureReposts') === 'true') ||
            false,
    );

    const { data: sextforceAutoRepostColors, isLoading: sextforceAutoRepostColorsLoading } = useSextforceAutoRepostColors();

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (!active) {
            setCurrentDate(moment());
            setSelectedDate(moment());
            setView('list');
        }
    }, [active]);

    const handlePrevMonth = () => {
        setCurrentDate(moment(currentDate).subtract(1, 'month'));
        localStorage.setItem('sextforceAutoRepostCalendarViewCurrentDate', moment(currentDate).subtract(1, 'month').format());
    };

    const handleNextMonth = () => {
        setCurrentDate(moment(currentDate).add(1, 'month'));
        localStorage.setItem('sextforceAutoRepostCalendarViewCurrentDate', moment(currentDate).add(1, 'month').format());
    };

    const handlePrevDay = () => {
        setSelectedDate(moment(selectedDate).subtract(1, 'day'));
        localStorage.setItem('sextforceAutoRepostCalendarViewSelectedDate', moment(selectedDate).subtract(1, 'day').format());

        // If the previous day is in the previous month, change the current month
        if (moment(selectedDate).subtract(1, 'day').isBefore(moment(currentDate).startOf('month'))) {
            setCurrentDate(moment(currentDate).subtract(1, 'month'));
            localStorage.setItem('sextforceAutoRepostCalendarViewCurrentDate', moment(currentDate).subtract(1, 'month').format());
        }
    };

    const handleNextDay = () => {
        setSelectedDate(moment(selectedDate).add(1, 'day'));
        localStorage.setItem('sextforceAutoRepostCalendarViewSelectedDate', moment(selectedDate).add(1, 'day').format());

        // If the next day is in the next month, change the current month
        if (moment(selectedDate).add(1, 'day').isAfter(moment(currentDate).endOf('month'))) {
            setCurrentDate(moment(currentDate).add(1, 'month'));
            localStorage.setItem('sextforceAutoRepostCalendarViewCurrentDate', moment(currentDate).add(1, 'month').format());
        }
    };

    const handleDateSelect = useCallback((date: moment.Moment) => {
        setSelectedDate(date);
        localStorage.setItem('sextforceAutoRepostCalendarViewSelectedDate', date.format());
        setView('day');
        localStorage.setItem('sextforceAutoRepostCalendarView', 'day');
    }, []);

    const handleViewChange = (event: React.SyntheticEvent, newValue: any) => {
        setView(newValue);

        localStorage.setItem('sextforceAutoRepostCalendarView', newValue);
    };

    // Get tasks originally scheduled for a specific date
    const getOriginalRepostsForDate = (date: moment.Moment): SextforceAutoRepostWithLatestRepostLog[] => {
        return autoReposts?.filter(task => moment(task.nextRepostDate).isSame(date, 'day')) || [];
    };

    // Fixed version of getAllTaskOccurrencesForDate for Auto Reposts
    const getAllTaskOccurrencesForDate = (date: moment.Moment): TaskOccurrence[] => {
        const targetDate = moment(date).startOf('day');
        const targetEndDate = moment(date).endOf('day');
        const occurrences: TaskOccurrence[] = [];

        // Add original tasks
        const originalTasks = getOriginalRepostsForDate(date);
        originalTasks.forEach(task => {
            occurrences.push({
                task,
                dateTime: moment(task.nextRepostDate),
                isRepeat: false,
            });
        });

        if (!showFutureReposts) {
            return occurrences;
        }

        // Add repeating occurrences - FIXED VERSION
        autoReposts?.forEach(task => {
            if (!task.repostEveryAmount || !task.repostEveryUnit) return;
            if (task.endDate && targetDate.isAfter(task.endDate)) return;

            const startDateTime = moment(task.nextRepostDate);

            // Skip if the target date is before the original repost date
            if (targetEndDate.isBefore(startDateTime)) return;

            // Start from the first repeat (not the original)
            let currentRepeat = startDateTime.clone().add(task.repostEveryAmount, task.repostEveryUnit);

            // Keep adding intervals until we're past our target day
            while (currentRepeat.isSameOrBefore(targetEndDate.clone().add(1, 'day'))) {
                // Check if this repeat falls within our target day
                if (currentRepeat.isSameOrAfter(targetDate) && currentRepeat.isSameOrBefore(targetEndDate)) {
                    // Check if we haven't exceeded the end date
                    if (!task.endDate || currentRepeat.isSameOrBefore(task.endDate)) {
                        occurrences.push({
                            task,
                            dateTime: currentRepeat.clone(),
                            isRepeat: true,
                        });
                    }
                }

                // Move to next repeat
                currentRepeat.add(task.repostEveryAmount, task.repostEveryUnit);

                // Safety break - don't calculate more than 100 repeats ahead
                if (currentRepeat.isAfter(moment().add(1, 'year'))) {
                    break;
                }
            }
        });

        // Sort by datetime
        return occurrences.sort((a, b) => a.dateTime.valueOf() - b.dateTime.valueOf());
    };

    const MAX_TOOLTIP_MESSAGES = 8;

    // Pretty paper-style tooltip container shared by all calendar chips
    const prettyTooltipSlotProps = {
        tooltip: {
            sx: {
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[4],
                borderRadius: `${theme.shape.borderRadius}px`,
                p: 0,
                maxWidth: 320,
            },
        },
    };

    const renderTooltipContent = (
        colorName: string,
        accentColor: string,
        subtitle: string | undefined,
        entries: { time: string; name: string }[],
    ) => {
        const visible = entries.slice(0, MAX_TOOLTIP_MESSAGES);
        const hiddenCount = entries.length - visible.length;

        return (
            <Box sx={{ p: 1.25, minWidth: 160 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: visible.length > 0 ? 0.75 : 0, minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: accentColor,
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, minWidth: 0 }} noWrap>
                        {colorName}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', flexShrink: 0 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="column" spacing={0.25}>
                    {visible.map((entry, index) => (
                        <Stack key={index} direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                {entry.time}
                            </Typography>
                            <Typography variant="caption" noWrap sx={{ minWidth: 0 }}>
                                {entry.name || 'Untitled repost'}
                            </Typography>
                        </Stack>
                    ))}
                    {hiddenCount > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ pt: 0.25 }}>
                            +{hiddenCount} more…
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    const renderTaskCount = (tasks: SextforceAutoRepostWithLatestRepostLog[], colorId: string, isRepeating: boolean = false) => {
        const backgroundColor =
            colorId === 'none' || sextforceAutoRepostColorsLoading || !sextforceAutoRepostColors
                ? alpha(theme.palette.primary.light, 0.5)
                : sextforceAutoRepostColors.find(c => c._id === colorId)?.color || alpha(theme.palette.primary.light, 0.5);
        const colorName = sextforceAutoRepostColors?.find(c => c._id === colorId)?.name || 'No category';
        const tooltipEntries = [...tasks]
            .sort((a, b) => moment(a.nextRepostDate).valueOf() - moment(b.nextRepostDate).valueOf())
            .map(task => ({ time: moment(task.nextRepostDate).format('LT'), name: task.name }));

        return (
            <Tooltip
                key={`${colorId}-${isRepeating}`}
                title={
                    isMobile
                        ? `${colorName}${isRepeating ? ' (Repeating)' : ''}`
                        : renderTooltipContent(colorName, backgroundColor, isRepeating ? 'Repeating' : undefined, tooltipEntries)
                }
                slotProps={isMobile ? undefined : prettyTooltipSlotProps}
            >
                <Box
                    sx={{
                        pl: 0.5,
                        pr: 0.5,
                        backgroundColor: isRepeating ? theme.palette.grey[300] : backgroundColor,
                        borderRadius: theme.shape.borderRadius,
                        width: '100%',
                        borderLeft: isRepeating ? `3px solid ${backgroundColor}` : 'none',
                    }}
                >
                    <Typography variant="caption" color={isRepeating ? backgroundColor : theme.palette.getContrastText(backgroundColor)}>
                        {tasks.length} {!isMobile && `repost${tasks.length > 1 ? 's' : ''}`}
                    </Typography>
                </Box>
            </Tooltip>
        );
    };

    const renderDayContent = (day: moment.Moment) => {
        const allOccurences = getAllTaskOccurrencesForDate(day);

        const originalTasks: SextforceAutoRepostWithLatestRepostLog[] = allOccurences
            .filter(occurrence => !occurrence.isRepeat)
            .map(occurrence => occurrence.task);

        const repeatingTasks: { task: SextforceAutoRepostWithLatestRepostLog; count: number }[] = allOccurences
            .filter(occurrence => occurrence.isRepeat)
            .reduce((acc, occurrence) => {
                const existingTask = acc.find(item => item.task._id === occurrence.task._id);
                if (existingTask) {
                    existingTask.count += 1;
                } else {
                    acc.push({ task: occurrence.task, count: 1 });
                }
                return acc;
            }, [] as { task: SextforceAutoRepostWithLatestRepostLog; count: number }[]);

        const originalGrouped = groupRepostsByColorId(originalTasks);
        const repeatingGrouped = repeatingTasks.reduce((acc, { task, count }) => {
            const colorId = task.colorId || 'none';
            if (!acc[colorId]) {
                acc[colorId] = { tasks: [], totalCount: 0 };
            }
            acc[colorId].tasks.push(task);
            acc[colorId].totalCount += count;
            return acc;
        }, {} as { [key: string]: { tasks: SextforceAutoRepostWithLatestRepostLog[]; totalCount: number } });

        return (
            <Stack direction="column" spacing={0.5} alignItems="center">
                {Object.entries(originalGrouped).map(([colorId, tasks]) => renderTaskCount(tasks, colorId))}
                {showFutureReposts &&
                    Object.entries(repeatingGrouped).map(([colorId, { tasks, totalCount }]) => {
                        const colorName = sextforceAutoRepostColors?.find(c => c._id === colorId)?.name || 'No category';
                        const accentColor =
                            colorId === 'none' || !sextforceAutoRepostColors
                                ? alpha(theme.palette.primary.light, 0.5)
                                : sextforceAutoRepostColors.find(c => c._id === colorId)?.color ||
                                  alpha(theme.palette.primary.light, 0.5);
                        const tooltipEntries = allOccurences
                            .filter(occurrence => occurrence.isRepeat && (occurrence.task.colorId || 'none') === colorId)
                            .map(occurrence => ({
                                time: occurrence.dateTime.format('LT'),
                                name: occurrence.task.name,
                            }));

                        return (
                            <Tooltip
                                key={`${colorId}-repeating`}
                                title={
                                    isMobile
                                        ? `${colorName} (${tasks.length} Auto Reposts repeating ${totalCount} ${
                                              totalCount === 1 ? 'time' : 'times'
                                          })`
                                        : renderTooltipContent(colorName, accentColor, 'Repeating', tooltipEntries)
                                }
                                slotProps={isMobile ? undefined : prettyTooltipSlotProps}
                            >
                                <Box
                                    sx={{
                                        pl: 0.5,
                                        pr: 0.5,
                                        backgroundColor: theme.palette.grey[300],
                                        borderRadius: theme.shape.borderRadius,
                                        width: '100%',
                                        borderLeft: `3px solid ${accentColor}`,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color={
                                            colorId === 'none' || !sextforceAutoRepostColors ? theme.palette.primary.light : accentColor
                                        }
                                    >
                                        {totalCount} {!isMobile && `repeat${totalCount > 1 ? 's' : ''}`}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        );
                    })}
            </Stack>
        );
    };

    const groupRepostsByColorId = (reposts: SextforceAutoRepostWithLatestRepostLog[]) => {
        const groupedReposts: { [key: string]: SextforceAutoRepostWithLatestRepostLog[] } = {};

        for (const repost of reposts) {
            if (!repost.colorId) {
                if (!groupedReposts['none']) {
                    groupedReposts['none'] = [];
                }

                groupedReposts['none'].push(repost);
                continue;
            }

            if (!groupedReposts[repost.colorId]) {
                groupedReposts[repost.colorId] = [];
            }

            groupedReposts[repost.colorId].push(repost);
        }

        return groupedReposts;
    };

    const renderMonthView = () => {
        const monthStart = moment(currentDate).startOf('month');
        const monthEnd = moment(currentDate).endOf('month');

        // Get the first day of the month (0-6, where 0 is Sunday)
        const firstDayOfMonth = monthStart.day();

        // Create array for all days including empty spots for previous month
        const days: (moment.Moment | null)[] = Array(firstDayOfMonth).fill(null);

        // Add all days of the month
        let currentDay = monthStart.clone();
        while (currentDay.isSameOrBefore(monthEnd)) {
            days.push(currentDay.clone());
            currentDay.add(1, 'day');
        }

        // Calculate remaining spots to fill out the last week
        const remainingDays = (7 - (days.length % 7)) % 7;
        days.push(...Array(remainingDays).fill(null));

        const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

        return (
            <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton onClick={handlePrevMonth} disabled={moment(currentDate).isSame(moment(), 'month')}>
                        <ChevronLeft />
                    </IconButton>
                    <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
                        {currentDate.format('MMMM YYYY')}
                    </Typography>
                    <IconButton onClick={handleNextMonth}>
                        <ChevronRight />
                    </IconButton>
                </Box>

                <Grid container spacing={0}>
                    {weekDays.map(day => (
                        <Grid item key={day} xs={12 / 7}>
                            <Typography align="center" variant="subtitle2">
                                {isMobile ? day.substring(0, 1) : day}
                            </Typography>
                        </Grid>
                    ))}

                    {days.map((day, index) => (
                        <Grid
                            item
                            xs={12 / 7}
                            key={index}
                            sx={{
                                borderTop: index < 7 ? `1px solid ${theme.palette.divider}` : 'none',
                                borderLeft: index % 7 !== 0 ? `1px solid ${theme.palette.divider}` : 'none',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            {day ? (
                                <Box
                                    onClick={() => handleDateSelect(day)}
                                    sx={{
                                        p: 0.5,
                                        minHeight: 80,
                                        height: '100%',
                                        cursor: 'pointer',
                                        backgroundColor: 'background.paper',
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        sx={{
                                            fontWeight: moment().isSame(day, 'day') ? 'bold' : 'normal',
                                        }}
                                    >
                                        {day.format('D')}
                                    </Typography>
                                    {renderDayContent(day)}
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        p: 1,
                                        minHeight: 80,
                                        height: '100%',
                                        backgroundColor: 'action.disabledBackground',
                                    }}
                                />
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    };

    return (
        <Box sx={{}}>
            <AppBar position="static" sx={{ mb: 2 }}>
                <Tabs indicatorColor="secondary" textColor="inherit" variant="fullWidth" value={view} onChange={handleViewChange} centered>
                    <Tab label="Month" value="month" disabled={!active} />
                    <Tab label="Day" value="day" disabled={!active} />
                    <Tab label="List" value="list" />
                </Tabs>
            </AppBar>

            {view === 'month' && renderMonthView()}
            {view === 'day' && (
                <SextforceAutoRepostCalendarDayView
                    subscriber={subscriber}
                    taskOccurrences={getAllTaskOccurrencesForDate(selectedDate)}
                    refetchAutoReposts={refetchAutoReposts}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    handlePrevDay={handlePrevDay}
                    handleNextDay={handleNextDay}
                    setAutoRepostInactiveStatus={setAutoRepostInactiveStatus}
                />
            )}
            {view === 'list' && (
                <SextforceAutoRepostCalendarListView
                    subscriber={subscriber}
                    autoReposts={autoReposts}
                    autoRepostsLoading={autoRepostsLoading}
                    refetchAutoReposts={refetchAutoReposts}
                    setSelectedDate={handleDateSelect}
                    sortBy={sortBy}
                    setAutoRepostInactiveStatus={setAutoRepostInactiveStatus}
                />
            )}

            <Stack direction="column" spacing={1} sx={{ mt: 2, pb: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            disabled={view === 'list'}
                            checked={showFutureReposts}
                            onChange={() => {
                                setShowFutureReposts(!showFutureReposts);
                                localStorage.setItem('sextforceAutoRepostCalendarShowFutureReposts', (!showFutureReposts).toString());
                            }}
                        />
                    }
                    label="Show future reposts"
                    sx={{ ml: 1 }}
                />
            </Stack>
        </Box>
    );
};

export default SextforceAutoRepostCalendar;
